import { Badge, Card, Divider, Group, Progress, Stack, Table, Text, Title } from '@mantine/core';
import type { MemberUsage } from '../../hooks/useUserDetail';

/**
 * SPRINT-0029 [8A-3] (D14) — what this member's model calls cost this UTC month, against the member
 * monthly cap (D12). Numbers and surface names only: the endpoint carries no prose and neither does
 * this panel.
 *
 * "Month to date" is the counter the cap is checked against. The per-surface rows are `api_usage`
 * priced on the same rate card; they sum to the counter unless a counter write failed or the
 * account was reset with the operator script (which clears the counter and keeps the ledger).
 */
export function formatUsd(microUsd: number): string {
  return `$${(microUsd / 1_000_000).toFixed(2)}`;
}

function capLine(usage: MemberUsage): string {
  if (usage.cap_micro_usd === null) return 'No cap set';
  if (usage.cap_micro_usd === 0) return 'Cap 0 — every call refused';
  return `${formatUsd(usage.micro_usd)} / ${formatUsd(usage.cap_micro_usd)}`;
}

export function UsagePanel({ usage }: { usage: MemberUsage }) {
  const cap = usage.cap_micro_usd;
  const atCap = cap !== null && usage.micro_usd >= cap;
  const percent = cap !== null && cap > 0 ? Math.min(100, (usage.micro_usd / cap) * 100) : cap === 0 ? 100 : 0;
  const surfaceTotal = usage.by_surface.reduce((sum, s) => sum + s.micro_usd, 0);

  return (
    <Card withBorder padding="md" data-testid="usage-panel">
      <Stack gap="xs">
        <Group justify="space-between">
          <Title order={6}>Model spend · {usage.month.slice(0, 7)} (UTC)</Title>
          {atCap && (
            <Badge size="xs" color="red" variant="light" data-testid="usage-at-cap">
              at cap
            </Badge>
          )}
        </Group>
        <Group justify="space-between" gap="xs">
          <Text size="sm" c="dimmed">
            Month to date
          </Text>
          <Text size="sm" data-testid="usage-month-to-date">
            {capLine(usage)}
          </Text>
        </Group>
        {cap !== null && (
          <Progress value={percent} color={atCap ? 'red' : 'teal'} size="sm" aria-label="Share of the monthly cap used" />
        )}
        <Group justify="space-between" gap="xs">
          <Text size="sm" c="dimmed">
            Calls
          </Text>
          <Text size="sm" data-testid="usage-calls">
            {usage.calls}
          </Text>
        </Group>

        <Divider my="xs" />
        {usage.by_surface.length === 0 ? (
          <Text size="sm" c="dimmed">
            No model calls this month
          </Text>
        ) : (
          <Table withRowBorders={false} verticalSpacing={2} data-testid="usage-by-surface">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Surface</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>Calls</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>Spend</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {usage.by_surface.map((s) => (
                <Table.Tr key={s.surface}>
                  <Table.Td>{s.surface}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{s.calls}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{formatUsd(s.micro_usd)}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
        {usage.by_surface.length > 0 && surfaceTotal !== usage.micro_usd && (
          <Text size="xs" c="dimmed" data-testid="usage-mismatch">
            Ledger total {formatUsd(surfaceTotal)} differs from the counter (a failed counter write or a test-account
            reset). See the reconciliation query in the internal-testing runbook.
          </Text>
        )}
      </Stack>
    </Card>
  );
}
